import type { ComponentProps } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { useTeam } from '@gonfalon/rest-api/hooks/useTeam';
import { Tag, TagFallback, TagIcon } from '@gonfalon/ui/TagGroup';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Icon } from '@launchpad-ui/icons';

type TagProps = ComponentProps<typeof Tag>;

export function TeamMaintainerTag({
  maintainerTeamKey,
  teamName,
  ...tagProps
}: { maintainerTeamKey: string; teamName?: string } & TagProps) {
  // When the caller already has the team name (e.g. from the flag's embedded
  // `_maintainerTeam`), render it directly and skip the fetch. GET /teams/{key}
  // requires the `viewTeam` permission, so refetching here would hide the
  // maintainer — or break the whole maintainer widget — for members without it.
  const shouldFetch = teamName === undefined;
  const { data: team, isLoading } = useTeam(
    { teamKey: maintainerTeamKey },
    { enabled: shouldFetch, throwOnError: false },
  );

  if (shouldFetch && isLoading) {
    return (
      <TagFallback
        aria-label={msg('maintainers.picker.loadingTeamLabel', { teamKey: maintainerTeamKey })}
        {...tagProps}
      >
        <TagFallback.Visual />
        <TagFallback.Text />
      </TagFallback>
    );
  }

  // Prefer the provided name, then the fetched name, and fall back to the key so
  // the tag still renders something meaningful when the team can't be read.
  const label = teamName ?? team?.name ?? maintainerTeamKey;

  return (
    <Tag
      aria-label={label}
      {...tagProps} // Forward RAC identity props (id, textValue) and other props
    >
      <TagIcon>
        <Icon name="group" size="small" />
      </TagIcon>{' '}
      <TextTruncator>{label}</TextTruncator>
    </Tag>
  );
}
